import React from 'react';
import { 
  X, 
  Sun, 
  Moon, 
  CloudRain, 
  Droplets, 
  Thermometer,
  Calendar,
  Info
} from 'lucide-react';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area
} from 'recharts';
import { proposalTokens } from './tokens';
import { getTemperatureUnitSymbol, convertTemperature } from '@/lib/utils';

interface ClimateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  destinationName: string;
  travelMonth?: string; // e.g., "May", "May-June"
  weatherData?: {
    day_avg_temperature: number | null;
    night_avg_temperature: number | null;
    rainfall: number | null;
    rainy_days: number | null;
    humidity: number | null;
    weather_description: string | null;
    travel_tip_weather: string | null;
    travel_tip_packing: string | null;
    travel_tip_local_events: string | null;
    annual_climate_data: Array<{
      month: string;
      avg_high: number;
      avg_low: number;
      rainfall: number;
    }> | null;
  } | null;
  temperatureUnit?: 'celsius' | 'fahrenheit' | null; // Temperature unit preference from event settings
}

// Default mock data for fallback
const defaultData = [
  { name: 'Jan', high: 15, low: 8, rain: 100 },
  { name: 'Feb', high: 16, low: 9, rain: 85 },
  { name: 'Mar', high: 18, low: 10, rain: 60 },
  { name: 'Apr', high: 20, low: 12, rain: 65 },
  { name: 'May', high: 22, low: 13, rain: 39 },
  { name: 'Jun', high: 26, low: 16, rain: 18 },
  { name: 'Jul', high: 28, low: 18, rain: 4 },
  { name: 'Aug', high: 28, low: 18, rain: 6 },
  { name: 'Sep', high: 26, low: 17, rain: 30 },
  { name: 'Oct', high: 23, low: 15, rain: 80 },
  { name: 'Nov', high: 18, low: 11, rain: 110 },
  { name: 'Dec', high: 15, low: 9, rain: 120 },
];

const TEAL = proposalTokens.colors.teal;
const GOLD = proposalTokens.colors.gold;
const CREAM = proposalTokens.colors.cream;

const ClimateReportModal: React.FC<ClimateReportModalProps> = ({ 
  isOpen, 
  onClose, 
  destinationName,
  travelMonth = 'May',
  weatherData = null,
  temperatureUnit = 'celsius'
}) => {
  if (!isOpen) return null;
  
  // Get temperature unit symbol for display
  const tempUnitSymbol = getTemperatureUnitSymbol(temperatureUnit);
  
  // Use weather data if available, otherwise use defaults (convert defaults if needed)
  const dayAvg = weatherData?.day_avg_temperature ?? (convertTemperature(22, temperatureUnit) ?? 22);
  const nightAvg = weatherData?.night_avg_temperature ?? (convertTemperature(13, temperatureUnit) ?? 13);
  const rainfallValue = weatherData?.rainfall ?? 39;
  const rainyDaysValue = weatherData?.rainy_days ?? 7;
  const humidityValue = weatherData?.humidity ?? 70;
  const notGenerated = 'Not yet generated.';
  const weatherDesc = weatherData?.weather_description || notGenerated;
  const tipWeather = weatherData?.travel_tip_weather || notGenerated;
  const tipPacking = weatherData?.travel_tip_packing || notGenerated;
  const tipLocalEvents = weatherData?.travel_tip_local_events || notGenerated;
  
  // Prepare chart data - use annual_climate_data if available, otherwise use defaults
  // Note: annual_climate_data is already converted in Destination component
  // If using defaultData, we need to convert it based on temperatureUnit
  const chartData = weatherData?.annual_climate_data && weatherData.annual_climate_data.length === 12
    ? weatherData.annual_climate_data.map(item => ({
        name: item.month,
        high: item.avg_high,
        low: item.avg_low,
        rain: item.rainfall,
      }))
    : defaultData.map(item => ({
        name: item.name,
        high: convertTemperature(item.high, temperatureUnit) ?? item.high,
        low: convertTemperature(item.low, temperatureUnit) ?? item.low,
        rain: item.rain,
      }));
  
  // Extract month name(s) for display
  const displayMonth = travelMonth.includes('-') 
    ? travelMonth.split('-')[0] // Use first month if spans two months
    : travelMonth;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-cream w-full max-w-5xl rounded-3xl shadow-2xl flex flex-col md:flex-row overflow-hidden max-h-[90vh] md:h-auto animate-fade-in relative"
        style={{ backgroundColor: CREAM }}
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" style={{ color: TEAL }} />
        </button>

        {/* Content Container - Scrollable on mobile, separate scrolls on desktop */}
        <div className="flex flex-col md:flex-row overflow-y-auto md:overflow-hidden flex-1">
          {/* Left Side: Report & Chart */}
          <div className="flex-1 p-6 md:p-10 md:overflow-y-auto bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Sun className="w-5 h-5" style={{ color: GOLD }} />
            <span className="font-bold text-sm tracking-wider uppercase" style={{ color: GOLD }}>Climate Report</span>
          </div>
          <h2 className="text-4xl font-serif font-bold mb-2" style={{ color: TEAL, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>{destinationName}</h2>
          
          <div className="mb-8">
            <h3 className="font-medium mb-3" style={{ color: `${TEAL}99` }}>Typical weather in <span className="font-bold" style={{ color: TEAL }}>{displayMonth}</span></h3>
            <div className="border-l-4 p-4 rounded-r-lg" style={{ backgroundColor: CREAM, borderColor: GOLD }}>
              <p className="italic" style={{ color: TEAL }}>
                {weatherDesc}
              </p>
            </div>
          </div>

          {/* 4 Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-4 flex flex-col items-center justify-center text-center">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${GOLD}1a` }}>
                <Sun className="w-5 h-5" style={{ color: GOLD }} />
              </div>
              <span className="text-xs uppercase font-bold mb-1" style={{ color: `${TEAL}66` }}>Day Avg</span>
              <span className="text-2xl font-bold" style={{ color: TEAL }}>
                {dayAvg !== null && dayAvg !== undefined ? `${Math.round(dayAvg)}${tempUnitSymbol}` : `22${tempUnitSymbol}`}
              </span>
            </div>
            
            <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-4 flex flex-col items-center justify-center text-center">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${TEAL}1a` }}>
                <Moon className="w-5 h-5" style={{ color: TEAL }} />
              </div>
              <span className="text-xs uppercase font-bold mb-1" style={{ color: `${TEAL}66` }}>Night Avg</span>
              <span className="text-2xl font-bold" style={{ color: TEAL }}>
                {nightAvg !== null && nightAvg !== undefined ? `${Math.round(nightAvg)}${tempUnitSymbol}` : `13${tempUnitSymbol}`}
              </span>
            </div>
            
            <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-4 flex flex-col items-center justify-center text-center">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${TEAL}1a` }}>
                <CloudRain className="w-5 h-5" style={{ color: TEAL }} />
              </div>
              <span className="text-xs uppercase font-bold mb-1" style={{ color: `${TEAL}66` }}>Rainfall</span>
              <span className="text-2xl font-bold" style={{ color: TEAL }}>{rainfallValue}<span className="text-sm font-normal" style={{ color: `${TEAL}66` }}>mm</span></span>
            </div>

            <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-4 flex flex-col items-center justify-center text-center">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${GOLD}1a` }}>
                <Droplets className="w-5 h-5" style={{ color: GOLD }} />
              </div>
              <span className="text-xs uppercase font-bold mb-1" style={{ color: `${TEAL}66` }}>Rainy Days</span>
              <div className="flex flex-col">
                <span className="text-2xl font-bold" style={{ color: TEAL }}>{rainyDaysValue}</span>
                <span className="text-[10px]" style={{ color: `${TEAL}66` }}>Humidity: {humidityValue}%</span>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm">
            <h3 className="font-bold mb-6" style={{ color: TEAL }}>Annual Climate Overview</h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 12 }} 
                    dy={10}
                  />
                  <YAxis 
                    yAxisId="left" 
                    orientation="left" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    unit={tempUnitSymbol}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    unit="mm"
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar yAxisId="right" dataKey="rain" fill={TEAL} radius={[4, 4, 0, 0]} barSize={12} name="Rainfall" />
                  <Line yAxisId="left" type="monotone" dataKey="high" stroke={GOLD} strokeWidth={3} dot={false} name="Avg High" />
                  <Line yAxisId="left" type="monotone" dataKey="low" stroke={TEAL} strokeOpacity={0.5} strokeWidth={3} strokeDasharray="5 5" dot={false} name="Avg Low" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: GOLD }}></div>
                <span className="text-xs" style={{ color: `${TEAL}99` }}>Avg High</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: `${TEAL}80` }}></div>
                <span className="text-xs" style={{ color: `${TEAL}99` }}>Avg Low</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TEAL }}></div>
                <span className="text-xs" style={{ color: `${TEAL}99` }}>Rainfall</span>
              </div>
            </div>
          </div>
        </div>

          {/* Right Side: Tips */}
          <div className="w-full md:w-[350px] p-8 md:border-l border-t md:border-t-0 border-black/5 md:overflow-y-auto" style={{ backgroundColor: CREAM }}>
            <div className="flex items-center gap-2 mb-6">
              <SparklesIcon className="w-5 h-5" style={{ color: GOLD }} />
              <h3 className="font-bold" style={{ color: TEAL }}>Smart Travel Tips</h3>
            </div>

            <p className="text-sm mb-6" style={{ color: `${TEAL}b3` }}>
              Here are three specific travel tips for your trip to {destinationName} in {displayMonth}:
            </p>

            <div className="space-y-6">
              <div className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ backgroundColor: GOLD }}></div>
                <div>
                  <span className="font-bold text-sm" style={{ color: TEAL }}>Weather:</span>
                  <p className="text-sm leading-relaxed mt-1" style={{ color: `${TEAL}cc` }}>
                    {tipWeather}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ backgroundColor: GOLD }}></div>
                <div>
                  <span className="font-bold text-sm" style={{ color: TEAL }}>Packing:</span>
                  <p className="text-sm leading-relaxed mt-1" style={{ color: `${TEAL}cc` }}>
                    {tipPacking}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ backgroundColor: GOLD }}></div>
                <div>
                  <span className="font-bold text-sm" style={{ color: TEAL }}>Local Events:</span>
                  <p className="text-sm leading-relaxed mt-1" style={{ color: `${TEAL}cc` }}>
                    {tipLocalEvents}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Icon for Sparkles
const SparklesIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
  </svg>
);

export default ClimateReportModal;

